<template>
  <HrPageLayout
    title="My Profile"
    subtitle="View your details"
    :breadcrumb-items="[
      { label: 'My Dashboard', route: '/employee-dashboard' },
      { label: 'My Profile' }
    ]"
  >
    <div class="employee-profile-content">
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading employee details...</p>
      </div>

      <template v-else-if="employee">
        <!-- Summary card -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body">
            <div class="profile-summary-grid">
              <div class="profile-avatar-wrap">
                <div class="profile-avatar">
                  {{ getInitials(employee.firstName, employee.lastName) }}
                </div>
              </div>
              <div class="profile-summary-main">
                <h4 class="hr-mb-1">{{ getFullName() }}</h4>
                <p class="hr-text-muted hr-mb-1">{{ employee.empNo || 'N/A' }}</p>
                <div class="hr-flex hr-gap-2 hr-flex-wrap hr-mb-2">
                  <span :class="['hr-badge', getStatusClass(employee.status)]">{{ employee.status || 'N/A' }}</span>
                  <span :class="['hr-badge', getEmploymentTypeClass(employee.employmentType)]">
                    {{ employee.employmentType || 'N/A' }}
                  </span>
                </div>
                <div class="profile-meta hr-flex hr-flex-wrap hr-gap-3 hr-text-muted">
                  <span><i class="bx bx-building-house"></i> {{ employee.departmentName || 'N/A' }}</span>
                  <span><i class="bx bx-briefcase"></i> {{ employee.positionTitle || 'N/A' }}</span>
                  <span><i class="bx bx-envelope"></i> {{ employee.email || 'N/A' }}</span>
                  <span><i class="bx bx-phone"></i> {{ employee.phone || 'N/A' }}</span>
                  <span><i class="bx bx-calendar"></i> Joined: {{ formatDate(employee.hireDate) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="hr-chart-card">
          <div class="hr-tabs-bar">
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'personal' }" @click="activeTab = 'personal'">
              <i class="bx bx-user"></i> Personal Info
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'governmentIds' }" @click="activeTab = 'governmentIds'">
              <i class="bx bx-id-card"></i> Government IDs <span class="hr-badge hr-badge-primary hr-badge-sm">{{ governmentIds.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'documents' }" @click="activeTab = 'documents'">
              <i class="bx bx-file"></i> Documents <span class="hr-badge hr-badge-primary hr-badge-sm">{{ documents.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'contacts' }" @click="activeTab = 'contacts'">
              <i class="bx bx-phone-call"></i> Emergency Contacts <span class="hr-badge hr-badge-warning hr-badge-sm">{{ emergencyContacts.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'kin' }" @click="activeTab = 'kin'">
              <i class="bx bx-heart"></i> Next of Kin <span class="hr-badge hr-badge-info hr-badge-sm">{{ nextOfKin.length }}</span>
            </button>
            <button type="button" class="hr-tab" :class="{ 'hr-tab-active': activeTab === 'integrations' }" @click="activeTab = 'integrations'">
              <i class="bx bx-plug"></i> Integrations
            </button>
          </div>

          <div class="hr-chart-body tab-content-wrap">
            <!-- Personal -->
            <div v-show="activeTab === 'personal'" class="tab-pane">
              <div class="hr-form-grid profile-detail-grid">
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-borderless">
                    <tbody>
                      <tr><td class="hr-label-cell">Employee Number</td><td>{{ employee.empNo || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">First Name</td><td>{{ employee.firstName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Middle Name</td><td>{{ employee.middleName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Last Name</td><td>{{ employee.lastName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Email</td><td><a :href="'mailto:' + employee.email">{{ employee.email || 'N/A' }}</a></td></tr>
                      <tr><td class="hr-label-cell">Personal Email</td><td><a v-if="employee.personalEmail" :href="'mailto:' + employee.personalEmail">{{ employee.personalEmail }}</a><span v-else>N/A</span></td></tr>
                      <tr><td class="hr-label-cell">Phone</td><td><a :href="'tel:' + employee.phone">{{ employee.phone || 'N/A' }}</a></td></tr>
                      <tr><td class="hr-label-cell">National ID</td><td>{{ employee.nationalId || 'N/A' }}</td></tr>
                    </tbody>
                  </table>
                </div>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-borderless">
                    <tbody>
                      <tr><td class="hr-label-cell">Gender</td><td>{{ employee.gender || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Date of Birth</td><td>{{ formatDate(employee.dateOfBirth) }}</td></tr>
                      <tr><td class="hr-label-cell">Nationality</td><td>{{ employee.nationality || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Department</td><td>{{ employee.departmentName || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Position</td><td>{{ employee.positionTitle || 'N/A' }}</td></tr>
                      <tr><td class="hr-label-cell">Employment Type</td><td><span :class="['hr-badge', getEmploymentTypeClass(employee.employmentType)]">{{ employee.employmentType || 'N/A' }}</span></td></tr>
                      <tr><td class="hr-label-cell">Hire Date</td><td>{{ formatDate(employee.hireDate) }}</td></tr>
                      <tr><td class="hr-label-cell">Status</td><td><span :class="['hr-badge', getStatusClass(employee.status)]">{{ employee.status || 'N/A' }}</span></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Government IDs -->
            <div v-show="activeTab === 'governmentIds'" class="tab-pane">
              <div v-if="governmentIds.length === 0" class="hr-empty-state">
                <i class="bx bx-id-card hr-empty-icon"></i>
                <h5>No government IDs available</h5>
                <p class="hr-text-muted">This employee has no government identification records on file</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(govId, index) in governmentIds" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon"><i class="bx bx-id-card"></i></div>
                    <span :class="['hr-badge', getIdTypeClass(govId.id_type)]">{{ formatIdType(govId.id_type) }}</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ formatIdType(govId.id_type) }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-hash"></i> <strong>Number:</strong> {{ govId.id_number || 'N/A' }}</p>
                    <p><i class="bx bx-flag"></i> <strong>Country:</strong> {{ govId.country_code || 'N/A' }}</p>
                    <p v-if="govId.issue_date"><i class="bx bx-calendar-check"></i> <strong>Issue Date:</strong> {{ formatDate(govId.issue_date) }}</p>
                    <p v-if="govId.expiry_date">
                      <i class="bx bx-calendar-event"></i> <strong>Expiry:</strong> {{ formatDate(govId.expiry_date) }}
                      <span v-if="isExpired(govId.expiry_date)" class="hr-badge hr-badge-danger hr-ml-1"><i class="bx bx-error-circle"></i> Expired</span>
                      <span v-else-if="isExpiringSoon(govId.expiry_date)" class="hr-badge hr-badge-warning hr-ml-1"><i class="bx bx-time-five"></i> Expiring Soon</span>
                    </p>
                    <p v-if="govId.notes" class="hr-fs-sm"><i class="bx bx-note"></i> {{ govId.notes }}</p>
                  </div>
                  <div class="hr-info-card-footer">
                    <span v-if="govId.is_verified === '1'" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Verified</span>
                    <span v-else class="hr-badge hr-badge-warning"><i class="bx bx-time"></i> Unverified</span>
                    <span v-if="govId.is_active === '1'" class="hr-badge hr-badge-info"><i class="bx bx-check"></i> Active</span>
                    <span v-else class="hr-badge hr-badge-secondary"><i class="bx bx-x"></i> Inactive</span>
                    <p v-if="govId.verified_by" class="hr-fs-sm hr-text-muted hr-mt-1">Verified by: {{ govId.verified_by }} on {{ formatDate(govId.verification_date) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents -->
            <div v-show="activeTab === 'documents'" class="tab-pane">
              <div v-if="documents.length === 0" class="hr-empty-state">
                <i class="bx bx-file hr-empty-icon"></i>
                <h5>No documents available</h5>
                <p class="hr-text-muted">This employee has no documents on file</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(doc, index) in documents" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon"><i class="bx bx-file"></i></div>
                    <span class="hr-badge hr-badge-secondary">{{ doc.documentType }}</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ doc.name || 'Untitled Document' }}</h6>
                  <div class="hr-info-card-body hr-fs-sm">
                    <p>Number: {{ doc.documentNumber || 'N/A' }}</p>
                    <p>Uploaded: {{ formatDate(doc.uploadDate) }}</p>
                    <p v-if="doc.validTill">Valid until: {{ formatDate(doc.validTill) }}</p>
                    <p v-if="doc.fileSize">Size: {{ formatFileSize(doc.fileSize) }}</p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-align-center hr-justify-between">
                    <span v-if="isDocumentApproved(doc)" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Approved</span>
                    <span v-else class="hr-badge hr-badge-warning"><i class="bx bx-time"></i> Pending</span>
                    <div class="hr-flex hr-gap-1">
                      <button v-if="doc.filePath" type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="viewDocument(doc)" title="View"><i class="bx bx-show"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary" title="Download"><i class="bx bx-download"></i></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Emergency Contacts -->
            <div v-show="activeTab === 'contacts'" class="tab-pane">
              <div v-if="emergencyContacts.length === 0" class="hr-empty-state">
                <i class="bx bx-phone-call hr-empty-icon"></i>
                <h5>No emergency contacts</h5>
                <p class="hr-text-muted">No emergency contacts have been added for this employee</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(contact, index) in emergencyContacts" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon hr-info-card-icon-warning"><i class="bx bx-user-circle"></i></div>
                    <span class="hr-badge hr-badge-warning">Emergency Contact</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ contact.name || 'N/A' }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-phone"></i> <a :href="'tel:' + contact.phoneNumber">{{ contact.phoneNumber || 'N/A' }}</a></p>
                    <p v-if="contact.email"><i class="bx bx-envelope"></i> <a :href="'mailto:' + contact.email">{{ contact.email }}</a></p>
                    <p><i class="bx bx-heart"></i> Relationship: {{ contact.relationship || 'N/A' }}</p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-gap-2">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="callContact(contact.phoneNumber)"><i class="bx bx-phone"></i> Call</button>
                    <button v-if="contact.email" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="emailContact(contact.email)"><i class="bx bx-envelope"></i> Email</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Next of Kin -->
            <div v-show="activeTab === 'kin'" class="tab-pane">
              <div v-if="nextOfKin.length === 0" class="hr-empty-state">
                <i class="bx bx-heart hr-empty-icon"></i>
                <h5>No next of kin records</h5>
                <p class="hr-text-muted">No next of kin information has been added for this employee</p>
              </div>
              <div v-else class="hr-cards-grid">
                <div v-for="(kin, index) in nextOfKin" :key="index" class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon hr-info-card-icon-info"><i class="bx bx-heart"></i></div>
                    <span class="hr-badge hr-badge-info">Next of Kin</span>
                  </div>
                  <h6 class="hr-info-card-title">{{ kin.name || 'N/A' }}</h6>
                  <div class="hr-info-card-body">
                    <p><i class="bx bx-phone"></i> <a :href="'tel:' + kin.phoneNumber">{{ kin.phoneNumber || 'N/A' }}</a></p>
                    <p v-if="kin.email"><i class="bx bx-envelope"></i> <a :href="'mailto:' + kin.email">{{ kin.email }}</a></p>
                    <p><i class="bx bx-heart"></i> Relationship: {{ kin.relationship || 'N/A' }}</p>
                    <p>
                      <span v-if="kin.isCoveredInBenefits === '1'" class="hr-badge hr-badge-success"><i class="bx bx-check-circle"></i> Covered in Benefits</span>
                      <span v-else class="hr-badge hr-badge-muted"><i class="bx bx-x-circle"></i> Not Covered</span>
                    </p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-gap-2">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="callContact(kin.phoneNumber)"><i class="bx bx-phone"></i> Call</button>
                    <button v-if="kin.email" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="emailContact(kin.email)"><i class="bx bx-envelope"></i> Email</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Integrations -->
            <div v-show="activeTab === 'integrations'" class="tab-pane">
              <div class="hr-cards-grid">
                
                <!-- Google Meet -->
                <div class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon" style="color: #4285F4;"><i class="bx bxl-google"></i></div>
                    <span class="hr-badge hr-badge-primary">Google Meet</span>
                  </div>
                  <h6 class="hr-info-card-title">Google Workspace</h6>
                  <div class="hr-info-card-body hr-fs-sm">
                    <p class="hr-text-muted">Generate Google Meet links automatically.</p>
                    <p v-if="integrations['Google Meet']?.connected" class="hr-text-success">
                      <i class='bx bx-check-circle'></i> Connected on {{ formatDate(integrations['Google Meet'].connectedAt) }}
                    </p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-align-center hr-justify-between">
                    <button v-if="integrations['Google Meet']?.connected" type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="disconnectIntegration('Google Meet')">Disconnect</button>
                    <button v-else type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="connectIntegration('Google Meet')">Connect</button>
                  </div>
                </div>

                <!-- Zoom -->
                <div class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon" style="color: #2D8CFF;"><i class="bx bx-video"></i></div>
                    <span class="hr-badge hr-badge-info">Zoom</span>
                  </div>
                  <h6 class="hr-info-card-title">Zoom Meetings</h6>
                  <div class="hr-info-card-body hr-fs-sm">
                    <p class="hr-text-muted">Create scheduled Zoom meetings instantly.</p>
                    <p v-if="integrations['Zoom']?.connected" class="hr-text-success">
                      <i class='bx bx-check-circle'></i> Connected on {{ formatDate(integrations['Zoom'].connectedAt) }}
                    </p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-align-center hr-justify-between">
                    <button v-if="integrations['Zoom']?.connected" type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="disconnectIntegration('Zoom')">Disconnect</button>
                    <button v-else type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="connectIntegration('Zoom')">Connect</button>
                  </div>
                </div>

                <!-- Microsoft Teams -->
                <div class="hr-info-card">
                  <div class="hr-info-card-header">
                    <div class="hr-info-card-icon" style="color: #6264A7;"><i class="bx bxl-microsoft-teams"></i></div>
                    <span class="hr-badge" style="background-color: #6264A7; color: white;">MS Teams</span>
                  </div>
                  <h6 class="hr-info-card-title">Microsoft Teams</h6>
                  <div class="hr-info-card-body hr-fs-sm">
                    <p class="hr-text-muted">Automate Teams meetings creation.</p>
                    <p v-if="integrations['Microsoft Teams']?.connected" class="hr-text-success">
                      <i class='bx bx-check-circle'></i> Connected on {{ formatDate(integrations['Microsoft Teams'].connectedAt) }}
                    </p>
                  </div>
                  <div class="hr-info-card-footer hr-flex hr-align-center hr-justify-between">
                    <button v-if="integrations['Microsoft Teams']?.connected" type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="disconnectIntegration('Microsoft Teams')">Disconnect</button>
                    <button v-else type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="connectIntegration('Microsoft Teams')">Connect</button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="hr-chart-card hr-mt-4">
          <div class="hr-chart-body hr-flex hr-justify-between hr-align-center hr-flex-wrap hr-gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="gotTo('employee-register')">
              <i class="bx bx-arrow-back"></i> Back to List
            </button>
            <button type="button" class="hr-btn hr-btn-primary" @click="editEmployee">
              <i class="bx bx-edit"></i> Edit Employee
            </button>
          </div>
        </div>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, uploads } from "@/api";

export default {
  name: "EmployeeProfile",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      activeTab: "personal",
      employee: null,
      governmentIds: [],
      documents: [],
      emergencyContacts: [],
      nextOfKin: [],
      integrations: {}
    };
  },

  mounted() {
    this.loadEmployeeDetails();
    this.loadIntegrations();
  },

  methods: {
    async loadEmployeeDetails() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeeProfile");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          const empData = res.data.data;
          this.employee = empData.employee;
          this.governmentIds = empData.governmentIds || [];
          this.documents = empData.documents || [];
          this.emergencyContacts = empData.emergencyContacts || [];
          this.nextOfKin = empData.nextOfKin || [];
        } else {
          throw new Error(res.data?.message || "Failed to load employee details");
        }
      } catch (error) {
        this.$toast?.error?.("Failed to load employee details: " + error.message) || console.error("Failed to load employee details:", error);
      } finally {
        this.loading = false;
      }
    },

    getInitials(firstName, lastName) {
      return ((firstName ? firstName.charAt(0) : "") + (lastName ? lastName.charAt(0) : "")).toUpperCase();
    },

    getFullName() {
      if (!this.employee) return "N/A";
      const parts = [this.employee.firstName, this.employee.middleName, this.employee.lastName].filter(Boolean);
      return parts.join(" ") || "N/A";
    },

    getStatusClass(status) {
      const s = status?.toLowerCase();
      if (s === "active") return "hr-badge-success";
      if (s === "resigned" || s === "terminated") return "hr-badge-danger";
      if (s === "suspended") return "hr-badge-warning";
      return "hr-badge-secondary";
    },

    getEmploymentTypeClass(type) {
      const map = { Permanent: "hr-badge-success", Contract: "hr-badge-info", Temporary: "hr-badge-warning", Intern: "hr-badge-primary", Consultant: "hr-badge-secondary" };
      return map[type] || "hr-badge-secondary";
    },

    formatIdType(idType) {
      const names = { NATIONAL_ID: "National ID", KRA_PIN: "KRA PIN", NHIF: "NHIF Number", NSSF: "NSSF Number", SHIF: "SHIF Number", PASSPORT: "Passport", DRIVING_LICENSE: "Driving License", BIRTH_CERT: "Birth Certificate" };
      return names[idType] || idType?.replace(/_/g, " ") || "Unknown";
    },

    getIdTypeClass(idType) {
      const map = { NATIONAL_ID: "hr-badge-primary", KRA_PIN: "hr-badge-success", NHIF: "hr-badge-info", NSSF: "hr-badge-warning", SHIF: "hr-badge-secondary", PASSPORT: "hr-badge-danger", DRIVING_LICENSE: "hr-badge-secondary", BIRTH_CERT: "hr-badge-muted" };
      return map[idType] || "hr-badge-secondary";
    },

    isExpired(date) {
      if (!date) return false;
      return new Date(date) < new Date();
    },

    isExpiringSoon(date) {
      if (!date) return false;
      const expiry = new Date(date);
      const now = new Date();
      const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return expiry > now && expiry <= in30;
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return "N/A";
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
    },

    isDocumentApproved(doc) {
      const v = doc?.isVerified;
      if (v === true || v === 1) return true;
      const s = String(v ?? "").trim().toLowerCase();
      return s === "1" || s === "true" || s === "yes" || s === "approved" || s === "verified";
    },

    viewDocument(doc) {
      if (doc.filePath) {
        window.open(uploads + "" + doc.filePath, "_blank");
      } else {
        this.$toast?.info?.("Document file not available") || alert("Document file not available");
      }
    },

    callContact(phoneNumber) {
      if (phoneNumber) window.location.href = `tel:${phoneNumber}`;
    },

    emailContact(email) {
      if (email) window.location.href = `mailto:${email}`;
    },

    editEmployee() {
      sessionStorage.setItem("employeeId", this.employee.id);
      this.gotTo("employee-edit");
    },

    gotTo(route) {
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      } else {
        this.$router.push({ name: route });
      }
    },

    async loadIntegrations() {
      try {
        const data = new FormData();
        data.append("function", "getUserIntegrations");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.integrations = res.data.data || {};
        }
      } catch (error) {
        console.error("Failed to load integrations:", error);
      }
    },

    async connectIntegration(platformName) {
      try {
        const data = new FormData();
        data.append("function", "connectIntegration");
        data.append("platformName", platformName);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success?.(res.data.message) || alert(res.data.message);
          this.loadIntegrations();
        } else {
          this.$toast?.error?.(res.data?.message || "Failed to connect") || alert("Failed to connect");
        }
      } catch (error) {
        this.$toast?.error?.("Error connecting integration") || alert("Error connecting integration");
      }
    },

    async disconnectIntegration(platformName) {
      if (!confirm(`Are you sure you want to disconnect ${platformName}?`)) return;
      try {
        const data = new FormData();
        data.append("function", "disconnectIntegration");
        data.append("platformName", platformName);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success?.(res.data.message) || alert(res.data.message);
          this.loadIntegrations();
        } else {
          this.$toast?.error?.(res.data?.message || "Failed to disconnect") || alert("Failed to disconnect");
        }
      } catch (error) {
        this.$toast?.error?.("Error disconnecting integration") || alert("Error disconnecting integration");
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.employee-profile-content { padding: 0; }

.profile-summary-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
}
@media (max-width: 768px) {
  .profile-summary-grid { grid-template-columns: 1fr; text-align: center; }
}

.profile-avatar-wrap { flex-shrink: 0; }
.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}
.profile-summary-main { min-width: 0; }
.profile-meta { font-size: 14px; }
.profile-meta i { margin-right: 6px; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-ml-1 { margin-left: 6px; }
.hr-mt-1 { margin-top: 4px; }
.hr-fs-sm { font-size: 12px; }

.hr-tabs-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 24px;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
}
.hr-tab {
  padding: 14px 18px;
  border: none;
  background: none;
  color: #64748b;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.2s, border-color 0.2s;
}
.hr-tab:hover { color: #4f46e5; }
.hr-tab-active { color: #4f46e5; border-bottom-color: #4f46e5; }
.hr-badge-sm { font-size: 11px; padding: 2px 8px; }

.tab-content-wrap { padding-top: 20px; }
.tab-pane { min-height: 120px; }

.profile-detail-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
@media (max-width: 768px) { .profile-detail-grid { grid-template-columns: 1fr; } }

.hr-table-borderless td { border: none; padding: 8px 0; vertical-align: top; }
.hr-label-cell { width: 40%; font-weight: 600; color: #64748b; }

.hr-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}
.hr-info-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  transition: box-shadow 0.2s;
}
.hr-info-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.hr-info-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
.hr-info-card-icon {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: #dbeafe; color: #2563eb; border-radius: 10px;
  font-size: 20px;
}
.hr-info-card-icon-warning { background: #fef3c7; color: #b45309; }
.hr-info-card-icon-info { background: #e0f2fe; color: #0369a1; }
.hr-info-card-title { margin: 0 0 12px; font-size: 16px; font-weight: 600; }
.hr-info-card-body p { margin: 0 0 8px; color: #64748b; font-size: 13px; }
.hr-info-card-body i { margin-right: 6px; }
.hr-info-card-footer { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e2e8f0; }

.hr-flex { display: flex; }
.hr-gap-1 { gap: 8px; }
.hr-gap-2 { gap: 12px; }
.hr-gap-3 { gap: 16px; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-align-center { align-items: center; }
.hr-justify-between { justify-content: space-between; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-mb-4 { margin-bottom: 16px; }
.hr-mt-4 { margin-top: 16px; }
</style>
